<template>
  <div class="voice-recognition">
    <el-tabs v-model="activeTab" class="demo-tabs">
      <el-tab-pane label="实时语音转写" name="realtime">
        <div class="realtime-section">
          <el-card>
            <template #header>
              <div class="card-header">
                <span>实时语音转写</span>
                <el-button 
                  :type="isRecording ? 'danger' : 'primary'"
                  @click="toggleRecording"
                >
                  {{ isRecording ? '停止录音' : '开始录音' }}
                </el-button>
              </div>
            </template>
            <div class="transcript-area">
              <el-input
                v-model="realtimeTranscript"
                type="textarea"
                :rows="10"
                placeholder="实时转写内容将显示在这里..."
                readonly
              />
            </div>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane label="文件语音识别" name="file">
        <div class="file-section">
          <el-card>
            <template #header>
              <div class="card-header">
                <span>文件语音识别</span>
              </div>
            </template>
            <el-upload
              class="upload-demo"
              drag
              action="#"
              :auto-upload="false"
              :on-change="handleFileChange"
              accept=".mp3,.wav,.m4a"
            >
              <el-icon class="el-icon--upload"><upload-filled /></el-icon>
              <div class="el-upload__text">
                拖拽文件到此处或 <em>点击上传</em>
              </div>
              <template #tip>
                <div class="el-upload__tip">
                  支持的音频格式：mp3, wav, m4a
                </div>
              </template>
            </el-upload>

            <div v-if="selectedFile" class="file-info">
              <p>已选择文件: {{ selectedFile.name }}</p>
              <el-button type="primary" @click="startFileRecognition" :loading="isRecognizing">
                {{ isRecognizing ? '识别中...' : '开始识别' }}
              </el-button>
            </div>

            <div class="transcript-area" v-if="fileTranscript">
              <el-input
                v-model="fileTranscript"
                type="textarea"
                :rows="10"
                placeholder="识别结果将显示在这里..."
                readonly
              />
            </div>
          </el-card>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'
import { UploadFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import CryptoJS from 'crypto-js'
import WebAudioSpeechRecognizer from '@/utils/asr/webaudiospeechrecognizer'

// 工具函数
function toUint8Array(wordArray) {
  const words = wordArray.words
  const sigBytes = wordArray.sigBytes
  const u8 = new Uint8Array(sigBytes)
  for (let i = 0; i < sigBytes; i++) {
    u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff
  }
  return u8
}

function Uint8ArrayToString(fileData) {
  let dataString = ''
  for (let i = 0; i < fileData.length; i++) {
    dataString += String.fromCharCode(fileData[i])
  }
  return dataString
}

function formatSignString(query, params) {
  let strParam = ""
  let signStr = "asr.cloud.tencent.com/asr/v2/"
  if (query['appid']) {
    signStr += query['appid']
  }
  const keys = Object.keys(params)
  keys.sort()
  for (let i = 0, len = keys.length; i < len; i++) {
    strParam += `&${keys[i]}=${params[keys[i]]}`
  }
  return `${signStr}?${strParam.slice(1)}`
}

const guid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

async function createQuery(query) {
  let params = {}
  const time = new Date().getTime()
  
  async function getServerTime() {
    return new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest()
        xhr.open("GET", 'https://asr.cloud.tencent.com/server_time', true)
        xhr.send()
        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4 && xhr.status === 200) {
            resolve(xhr.responseText)
          }
        }
      } catch (error) {
        reject(error)
      }
    })
  }
  
  const serverTime = await getServerTime()
  params['secretid'] = query.secretid || ''
  params['engine_model_type'] = query.engine_model_type || '16k_zh'
  params['timestamp'] = parseInt(serverTime) || Math.round(time / 1000)
  params['expired'] = Math.round(time / 1000) + 24 * 60 * 60
  params['nonce'] = Math.round(time / 100000)
  params['voice_id'] = guid()
  params['voice_format'] = query.voice_format || 1
  
  const tempQuery = { ...query }
  const needFiltrationParams = ['appid', 'secretkey', 'signCallback']
  for (let i = 0, len = needFiltrationParams.length; i < len; i++) {
    if (tempQuery.hasOwnProperty(needFiltrationParams[i])) {
      delete tempQuery[needFiltrationParams[i]]
    }
  }
  
  params = {
    ...tempQuery,
    ...params,
  }
  return params
}

// 签名函数
function signCallback(secretKey, signStr) {
  const hash = CryptoJS.HmacSHA1(signStr, secretKey)
  const bytes = Uint8ArrayToString(toUint8Array(hash))
  return window.btoa(bytes)
}

// 获取签名URL
async function getUrl(params) {
  if (!params.appid || !params.secretid) {
    ElMessage.error('请确认是否填入账号信息')
    return false
  }
  const urlQuery = await createQuery(params)
  const queryStr = formatSignString(params, urlQuery)
  let signature = ''
  if (params.signCallback) {
    signature = params.signCallback(queryStr)
  } else {
    signature = signCallback(params.secretkey, queryStr)
  }
  return `wss://${queryStr}&signature=${encodeURIComponent(signature)}`
}

// 状态变量
const activeTab = ref('realtime')
const isRecording = ref(false)
const isRecognizing = ref(false)
const realtimeTranscript = ref('')
const selectedFile = ref(null)
const fileTranscript = ref('')
const currentSentence = ref('')
let recognizer = null

// 实时录音相关方法
const initAudioRecording = async () => {
  try {
    // 构建参数
    const params = {
      secretid: process.env.VUE_APP_TENCENT_SECRET_ID,
      secretkey: process.env.VUE_APP_TENCENT_SECRET_KEY,
      appid: Number(process.env.VUE_APP_TENCENT_APPID),
      engine_model_type: '16k_zh',
      voice_format: 1,
      hotword_id: '',
      needvad: 1,
      filter_dirty: 0,
      filter_modal: 0,
      filter_punc: 0,
      convert_num_mode: 1,
      word_info: 0
    }

    // 创建识别器实例
    recognizer = new WebAudioSpeechRecognizer(params, true)

    // 开始识别回调
    recognizer.OnRecognitionStart = (res) => {
      console.log('开始识别:', res)
      isRecording.value = true
      ElMessage.success('开始录音')
    }

    // 一句话开始
    recognizer.OnSentenceBegin = (res) => {
      console.log('一句话开始:', res)
      if (res.result && res.result.voice_text_str) {
        currentSentence.value = res.result.voice_text_str
        // 实时显示当前句子
        realtimeTranscript.value = realtimeTranscript.value + currentSentence.value
      }
    }

    // 识别结果变化
    recognizer.OnRecognitionResultChange = (res) => {
      console.log('识别结果变化:', res)
      if (res.result && res.result.voice_text_str) {
        // 更新当前句子
        const newText = res.result.voice_text_str
        // 移除上一次的临时结果
        if (currentSentence.value) {
          realtimeTranscript.value = realtimeTranscript.value.slice(0, -currentSentence.value.length)
        }
        currentSentence.value = newText
        // 添加新的临时结果
        realtimeTranscript.value = realtimeTranscript.value + currentSentence.value
      }
    }

    // 一句话结束
    recognizer.OnSentenceEnd = (res) => {
      console.log('一句话结束:', res)
      if (res.result && res.result.voice_text_str) {
        // 移除上一次的临时结果
        if (currentSentence.value) {
          realtimeTranscript.value = realtimeTranscript.value.slice(0, -currentSentence.value.length)
        }
        // 添加最终结果
        currentSentence.value = res.result.voice_text_str
        realtimeTranscript.value = realtimeTranscript.value + currentSentence.value + '\n'
        currentSentence.value = ''
      }
    }

    // 识别完成
    recognizer.OnRecognitionComplete = (res) => {
      console.log('识别完成:', res)
      stopRecording()
    }

    // 识别错误
    recognizer.OnError = (error) => {
      console.error('识别错误:', error)
      ElMessage.error('识别错误：' + (error.message || '未知错误'))
      stopRecording()
    }

    // 录音停止
    recognizer.OnRecorderStop = () => {
      console.log('录音停止')
      isRecording.value = false
      ElMessage.success('停止录音')
    }

    // 开始识别
    recognizer.start()

  } catch (error) {
    console.error('录音初始化失败:', error)
    ElMessage.error('启动录音失败：' + error.message)
    stopRecording()
  }
}

// 停止录音方法
const stopRecording = () => {
  if (recognizer) {
    recognizer.stop()
    recognizer = null
  }
  isRecording.value = false
}

// 切换录音方法
const toggleRecording = async () => {
  if (!isRecording.value) {
    realtimeTranscript.value = ''
    currentSentence.value = ''
    await initAudioRecording()
  } else {
    stopRecording()
  }
}

// 文件上传相关方法
const handleFileChange = (file) => {
  selectedFile.value = file.raw
}

const startFileRecognition = async () => {
  if (!selectedFile.value) {
    ElMessage.warning('请先选择音频文件')
    return
  }

  isRecognizing.value = true
  try {
    const formData = new FormData()
    formData.append('file', selectedFile.value)
    
    // 发送文件到后端进行识别
    const response = await fetch('/api/voice/recognize', {
      method: 'POST',
      body: formData
    })
    
    const result = await response.json()
    if (result.code === 0) {
      fileTranscript.value = result.content.text
      ElMessage.success('识别完成')
    } else {
      throw new Error(result.message || '识别失败')
    }
  } catch (error) {
    ElMessage.error('文件识别失败：' + error.message)
  } finally {
    isRecognizing.value = false
  }
}

// 组件卸载时清理资源
onUnmounted(() => {
  if (recognizer) {
    recognizer.destroyStream()
    recognizer = null
  }
})
</script>

<style scoped>
.voice-recognition {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.transcript-area {
  margin-top: 20px;
}

.file-info {
  margin: 20px 0;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.upload-demo {
  margin: 20px 0;
}
</style> 